import { gapsAround, type Period } from "@wmf/shared/workingHours";
import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from "react";
import {
  diaryPaymentIndicator,
  type DiaryPaymentClassification,
} from "../booking/diaryPayments.js";
import type { ScheduleBlockTarget } from "../booking/scheduling.js";
import { dayKey, formatDate, formatTime, londonWallClockAttempt } from "../format.js";
import { BlankSlotDrawer } from "./BlankSlotDrawer.js";
import { BookLessonDrawer, type BookLessonHint } from "./BookLessonDrawer.js";
import { missingBookingSuggestionRange } from "./bookingSuggestions.js";
import { describeCalendarBlock, expandHourRangeForBlocks, isVisibleCalendarBlock, type CalendarBlockSpan } from "./blocks.js";
import { colourOf, hasColour, isNoShow, isNoShowSession, type DiaryEntry } from "./entries.js";
import {
  SLOT_MINUTES,
  hoursShown,
  minutesOfDay,
  opensAt,
  rowsFor,
  sessionsByColumn,
  type Session,
} from "./grid.js";
import { layOutCalendarSpans } from "./laneLayout.js";
import { LessonActionDrawer } from "./LessonActionDrawer.js";
import type { ScheduleChange } from "./pendingPatches.js";
import { useDiaryClock } from "./useDiaryClock.js";
import "./timePosition.css";

/** One column of the grid, and what its heading says. */
export interface Column {
  /** What `columnOf` returns for anything belonging here. */
  key: string;
  heading: string;
  /** The second half of the heading, bolder. A day number, or nothing. */
  subheading?: string;
  /** Marked out — today, in the week. */
  highlight?: boolean;
  /** London calendar day represented by this column, for empty-cell/drop entry points. */
  day?: string;
  /** Tutor profile represented by or selected for this column, when unambiguous. */
  tutorId?: string;
}

export interface BlankDiarySlot {
  startsAt: string;
  minute: number;
  day: string;
  tutorId?: string;
}

interface BlankSlotPosition {
  columnKey: string;
  minute: number;
}

interface SelectedLesson {
  session: Session<DiaryEntry>;
  /** A drag opens the shared drawer directly in Reschedule; a click opens details. */
  initialAction: "move" | null;
  /** Drag target. Prefill only; the move flow still previews before mutation. */
  draggedTo: string | null;
}

type CalendarGridItem =
  | {
      kind: "lesson";
      startMinutes: number;
      endMinutes: number;
      session: Session<DiaryEntry>;
    }
  | {
      kind: "block";
      startMinutes: number;
      endMinutes: number;
      block: CalendarBlockSpan;
    };

/**
 * The provider's numeric appointment id for a session, or null for a lesson
 * that did not come from Amelia as one.
 *
 * `session.appointmentId` is the Hub's own UUID — safe as a React key and as
 * the drag-and-drop correlation token, but not what payment classification
 * keys on. That wants Amelia's numeric id, carried per booking as `externalId`.
 */
function ameliaAppointmentIdOf(session: Session<DiaryEntry>): number | null {
  const first = session.bookings.find(
    (entry): entry is DiaryEntry & { anonymous: false } => !entry.anonymous,
  );
  return isAmeliaAppointmentId(first?.externalId) ? first.externalId : null;
}

function isAmeliaAppointmentId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function blockedTimeLabel(block: CalendarBlockSpan): string {
  if (block.startTime === null) return "Blocked · all day";
  return `Blocked · ${block.startTime.slice(0, 5)}–${block.endTime?.slice(0, 5) ?? ""}`;
}

/**
 * Time down the side, columns across the top, lessons drawn to scale.
 *
 * Calendar interaction is deliberately only an entry point. Empty cells open
 * the canonical one-lesson booking flow inside the slot drawer, alongside the
 * reusable schedule-block actions; lesson clicks and drags open the shared
 * appointment drawer used by list/month and student records. No booking,
 * payment or schedule mutation is implemented by this component itself.
 *
 * Closed-hour shading is presentation, not a client-side permission gate.
 * Clicking or dropping there reaches the normal booking/reschedule flow, which
 * asks WMF-SIte whether the exact instant is a safe schedule override or a
 * genuine conflict. Explicit blocks remain visible and intact, but the diary
 * offers an explicit booking action over them as well.
 */
export function TimeGrid({
  payments = new Map(),
  refreshAll,
  refreshOne,
  columnOf,
  columns,
  entries,
  hoursFor,
  blocksFor,
  onBlockClick,
  onBlockCreated,
  resolveBlockTarget,
  blockTargetLoading = false,
  blockTargetError = null,
  blockBusyId = null,
  wholeDay = false,
  focusAppointmentId = null,
  focusMinute = null,
  onChanged,
  onScheduleChanged = onChanged,
}: {
  payments?: ReadonlyMap<number, DiaryPaymentClassification>;
  refreshAll?: () => void;
  refreshOne?: (appointmentId: number) => Promise<void>;
  columnOf: (entry: DiaryEntry) => string;
  columns: Column[];
  entries: DiaryEntry[];
  /**
   * When this column's tutor is available, drawn behind the lessons.
   * Undefined means availability is unknown rather than closed.
   */
  hoursFor?: (key: string) => Period[] | undefined;
  /** Active provider schedule blocks projected onto this column/day. */
  blocksFor?: (key: string) => CalendarBlockSpan[];
  /** Opens the block actions owned by the page. */
  onBlockClick?: (block: CalendarBlockSpan, bookingHint: BookLessonHint | null) => void;
  /** Re-read the authoritative block layer after the drawer creates a block. */
  onBlockCreated?: (providerId: number) => void;
  /**
   * Resolve which provider a blank slot's diary tutor may be blocked against.
   * Supplied by the page, which already holds the scheduling identity — the
   * drawer never re-fetches it.
   */
  resolveBlockTarget?: (tutorId: string | undefined) => ScheduleBlockTarget | null;
  /** The page is still loading the identity `resolveBlockTarget` needs. */
  blockTargetLoading?: boolean;
  /** Loading that identity failed. */
  blockTargetError?: string | null;
  /** The block currently being removed, if any. */
  blockBusyId?: string | null;
  /** Midnight to midnight, rather than only the hours something is booked in. */
  wholeDay?: boolean;
  /** A successful publication returns here and reveals this lesson once. */
  focusAppointmentId?: string | null;
  /** With no next lesson, show the time of the return instead of the morning. */
  focusMinute?: number | null;
  /** Re-read the authoritative mirror after an existing lesson/payment mutation closes. */
  onChanged?: () => void;
  /** Confirmed booking, cancellation or move; dismissals never call this. */
  onScheduleChanged?: (change?: ScheduleChange) => void;
}) {
  const now = useDiaryClock();
  const today = dayKey(now);
  const nowMinutes = minutesOfDay(now);
  const [selected, setSelected] = useState<SelectedLesson | null>(null);
  const [selectedBlank, setSelectedBlank] = useState<BlankDiarySlot | null>(null);
  const [pointerCursor, setPointerCursorState] = useState<BlankSlotPosition | null>(null);
  const [keyboardCursor, setKeyboardCursor] = useState<BlankSlotPosition | null>(null);
  const [keyboardFocusColumn, setKeyboardFocusColumn] = useState<string | null>(null);
  const [bookingHint, setBookingHint] = useState<BookLessonHint | null>(null);
  const touchGesture = useRef<{ pointerId: number; startY: number } | null>(null);
  const suppressNextBlankClick = useRef(false);
  const pointerCursorRef = useRef<BlankSlotPosition | null>(null);
  const pointerRect = useRef<{ columnKey: string; top: number; height: number } | null>(null);

  /** Clear and re-read payment truth alongside the authoritative mirror. */
  const refresh = (changed = onChanged) => {
    refreshAll?.();
    changed?.();
  };

  const byColumn = sessionsByColumn(
    entries,
    columns.map((column) => column.key),
    columnOf,
  );
  const loadedDays = columns.flatMap((column) => column.day ? [column.day] : []).sort();
  const loadedBookingRange = loadedDays.length > 0
    ? { from: loadedDays[0]!, to: loadedDays[loadedDays.length - 1]! }
    : null;
  const sessions = [...byColumn.values()].flat();
  const selectedAppointmentId = selected?.session.appointmentId ?? null;
  useEffect(() => {
    if (selectedAppointmentId === null) return;
    const refreshedSessions = [...sessionsByColumn(
      entries,
      columns.map((column) => column.key),
      columnOf,
    ).values()].flat();
    const refreshedSession = refreshedSessions.find(
      (session) => session.appointmentId === selectedAppointmentId,
    );
    if (!refreshedSession) return;
    setSelected((current) => {
      if (current === null || current.session.appointmentId !== selectedAppointmentId ||
        current.session === refreshedSession) return current;
      return { ...current, session: refreshedSession };
    });
  }, [columns, columnOf, entries, selectedAppointmentId]);

  async function refreshSelectedInPlace(session: Session<DiaryEntry>) {
    const appointmentId = ameliaAppointmentIdOf(session);
    if (appointmentId !== null) await refreshOne?.(appointmentId);
    onChanged?.();
  }

  const blockedByColumn = new Map(
    columns.map((column) => [column.key, blocksFor?.(column.key) ?? []] as const),
  );
  const visibleBlocks = [...blockedByColumn.values()].flat().filter(isVisibleCalendarBlock);
  const range = expandHourRangeForBlocks(hoursShown(sessions, wholeDay), wholeDay ? [] : visibleBlocks);
  const rows = ((range.endHour - range.startHour) * 60) / SLOT_MINUTES;
  const hours = Array.from(
    { length: range.endHour - range.startHour },
    (_, index) => range.startHour + index,
  );
  const nowPosition = columns.some((column) => column.day === today)
    && nowMinutes >= range.startHour * 60 && nowMinutes < range.endHour * 60
    ? `${((nowMinutes - range.startHour * 60) / ((range.endHour - range.startHour) * 60)) * 100}%`
    : null;
  const shape = { "--columns": `${columns.length}` } as React.CSSProperties;
  const box = useRef<HTMLDivElement>(null);
  const revealedAppointment = useRef<string | null>(null);
  const lastPositionedWindow = useRef<string | null>(null);
  const scrollWindowKey = `${columns.map((column) => column.key).join()}${wholeDay}`;
  const opening = opensAt(sessions);

  useEffect(() => {
    if (lastPositionedWindow.current === scrollWindowKey) return;
    lastPositionedWindow.current = scrollWindowKey;
    const node = box.current;
    const body = node?.querySelector(".cal-week-body");
    if (!node || !body || !wholeDay) return;
    const slot = body.getBoundingClientRect().height / rows;
    node.scrollTop = Math.max(0, (((focusMinute ?? opening) - 60) / SLOT_MINUTES) * slot - 8);
    // Opening and row geometry can change after a move; keep the user's view
    // stable until the columns or whole-day mode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollWindowKey]);

  useEffect(() => {
    if (!focusAppointmentId || revealedAppointment.current === focusAppointmentId) return;
    const lesson = Array.from(box.current?.querySelectorAll<HTMLElement>("[data-appointment-id]") ?? [])
      .find((node) => node.dataset.appointmentId === focusAppointmentId);
    if (!lesson) return;
    lesson.scrollIntoView({ block: "center", inline: "nearest" });
    lesson.focus({ preventScroll: true });
    revealedAppointment.current = focusAppointmentId;
  }, [focusAppointmentId, entries, columns]);

  useEffect(() => {
    // A range change alters the pointer-to-row geometry. The next pointer move
    // takes one fresh measurement instead of carrying a stale rectangle.
    pointerRect.current = null;
  }, [range.startHour, range.endHour, rows]);

  function slotAtMinute(column: Column, minute: number): BlankDiarySlot | null {
    if (!column.day) return null;
    const startMinute = range.startHour * 60;
    const endMinute = range.endHour * 60;
    if (minute < startMinute || minute >= endMinute) return null;

    const hour = Math.floor(minute / 60);
    const minutes = minute % 60;
    const attempt = londonWallClockAttempt(
      column.day,
      `${String(hour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    );
    if (!attempt.ok) return null;

    return {
      startsAt: attempt.iso,
      minute,
      day: column.day,
      tutorId: column.tutorId,
    };
  }

  function isFreeSlot(column: Column, minute: number): boolean {
    const end = minute + SLOT_MINUTES;
    const occupied = (byColumn.get(column.key) ?? []).some(
      (session) => minute < session.endMinutes && end > session.startMinutes,
    );
    if (occupied) return false;

    return !(blockedByColumn.get(column.key) ?? []).some(
      (block) => minute < block.endMinutes && end > block.startMinutes,
    );
  }

  function freeSlotAtMinute(column: Column, minute: number): BlankDiarySlot | null {
    if (!isFreeSlot(column, minute)) return null;
    return slotAtMinute(column, minute);
  }

  function minuteAtRect(rect: { top: number; height: number }, clientY: number): number | null {
    if (rows <= 0 || rect.height <= 0) return null;
    const row = Math.max(0, Math.min(rows - 1, Math.floor(((clientY - rect.top) / rect.height) * rows)));
    return range.startHour * 60 + row * SLOT_MINUTES;
  }

  /** Which exact quarter-hour a pointer is over, expressed as a real London instant. */
  function targetAt(column: Column, node: HTMLElement, clientY: number): { startsAt: string; minute: number } | null {
    if (!column.day) return null;
    const minute = minuteAtRect(node.getBoundingClientRect(), clientY);
    if (minute === null) return null;
    const slot = slotAtMinute(column, minute);
    return slot ? { startsAt: slot.startsAt, minute: slot.minute } : null;
  }

  function freeSlotAtPointer(column: Column, node: HTMLElement, clientY: number): BlankDiarySlot | null {
    const target = targetAt(column, node, clientY);
    return target ? freeSlotAtMinute(column, target.minute) : null;
  }

  function firstFreeSlot(column: Column, fromEnd = false): BlankDiarySlot | null {
    const start = range.startHour * 60;
    const end = range.endHour * 60;
    if (fromEnd) {
      for (let minute = end - SLOT_MINUTES; minute >= start; minute -= SLOT_MINUTES) {
        const slot = freeSlotAtMinute(column, minute);
        if (slot) return slot;
      }
      return null;
    }

    for (let minute = start; minute < end; minute += SLOT_MINUTES) {
      const slot = freeSlotAtMinute(column, minute);
      if (slot) return slot;
    }
    return null;
  }

  function showPointerCursor(next: BlankSlotPosition | null) {
    const current = pointerCursorRef.current;
    if (
      current?.columnKey === next?.columnKey
      && current?.minute === next?.minute
    ) return;
    pointerCursorRef.current = next;
    setPointerCursorState(next);
  }

  function showKeyboardCursor(next: BlankSlotPosition | null) {
    setKeyboardCursor((current) => {
      if (
        current?.columnKey === next?.columnKey
        && current?.minute === next?.minute
      ) return current;
      return next;
    });
  }

  function openBlankSlot(slot: BlankDiarySlot) {
    // A blank cell opens a chooser even when it is shaded as outside normal
    // hours. Booking rechecks the exact instant with WMF-SIte before offering
    // any override. Block creation keeps its own schedule-block contract.
    setSelected(null);
    setBookingHint(null);
    showPointerCursor(null);
    showKeyboardCursor(null);
    setKeyboardFocusColumn(null);
    setSelectedBlank(slot);
  }

  function openBlankCell(event: MouseEvent<HTMLDivElement>, column: Column) {
    if (suppressNextBlankClick.current) {
      suppressNextBlankClick.current = false;
      return;
    }

    const target = event.target as Element;
    if (target.closest(".cal-session") || target.closest(".cal-block")) return;
    const slot = freeSlotAtPointer(column, event.currentTarget, event.clientY);
    if (!slot) return;
    openBlankSlot(slot);
  }

  function pointerMinuteAt(column: Column, node: HTMLElement, clientY: number): number | null {
    let cached = pointerRect.current;
    if (!cached || cached.columnKey !== column.key) {
      const rect = node.getBoundingClientRect();
      if (rect.height <= 0) return null;
      cached = { columnKey: column.key, top: rect.top, height: rect.height };
      pointerRect.current = cached;
    }
    return minuteAtRect(cached, clientY);
  }

  function trackBlankPointer(event: PointerEvent<HTMLDivElement>, column: Column) {
    if (event.pointerType === "touch") {
      const gesture = touchGesture.current;
      if (gesture?.pointerId === event.pointerId && Math.abs(event.clientY - gesture.startY) > 8) {
        suppressNextBlankClick.current = true;
      }
      return;
    }

    const target = event.target as Element;
    if (target.closest(".cal-session") || target.closest(".cal-block")) {
      showPointerCursor(null);
      return;
    }

    // Geometry is measured once per pointer entry/range, and the expensive
    // London wall-clock conversion happens only when React renders a new row.
    const minute = pointerMinuteAt(column, event.currentTarget, event.clientY);
    showPointerCursor(minute === null ? null : { columnKey: column.key, minute });
  }

  function startBlankPointer(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType === "touch") {
      touchGesture.current = { pointerId: event.pointerId, startY: event.clientY };
    } else {
      touchGesture.current = null;
    }
    suppressNextBlankClick.current = false;
  }

  function finishBlankPointer(event: PointerEvent<HTMLDivElement>, cancelled = false) {
    if (event.pointerType === "touch" && touchGesture.current?.pointerId === event.pointerId) {
      touchGesture.current = null;
    }
    if (cancelled) {
      suppressNextBlankClick.current = event.pointerType === "touch";
      pointerRect.current = null;
      showPointerCursor(null);
    }
  }

  function moveKeyboardSlot(column: Column, currentMinute: number, direction: -1 | 1): BlankDiarySlot | null {
    const start = range.startHour * 60;
    const end = range.endHour * 60;
    for (
      let minute = currentMinute + direction * SLOT_MINUTES;
      minute >= start && minute < end;
      minute += direction * SLOT_MINUTES
    ) {
      const slot = freeSlotAtMinute(column, minute);
      if (slot) return slot;
    }
    return null;
  }

  function activateKeyboardSlot(column: Column, slot: BlankDiarySlot) {
    // Re-read the current render's session/block projections immediately before
    // activation. A live diary refresh may have filled the row since focus.
    const fresh = freeSlotAtMinute(column, slot.minute);
    if (fresh) {
      openBlankSlot(fresh);
      return;
    }

    const fallback = firstFreeSlot(column);
    showKeyboardCursor(fallback ? { columnKey: column.key, minute: fallback.minute } : null);
  }

  function handleBlankKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    column: Column,
    slot: BlankDiarySlot,
  ) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const next = moveKeyboardSlot(column, slot.minute, event.key === "ArrowDown" ? 1 : -1);
      if (next) showKeyboardCursor({ columnKey: column.key, minute: next.minute });
      return;
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const next = firstFreeSlot(column, event.key === "End");
      if (next) showKeyboardCursor({ columnKey: column.key, minute: next.minute });
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    activateKeyboardSlot(column, slot);
  }

  function blankSlotLabel(column: Column, slot: BlankDiarySlot): string {
    return `${column.heading}, ${formatDate(slot.startsAt)} at ${formatTime(slot.startsAt)}. Create an action: Book lesson or Block time. Use Up and Down arrow keys to move by 15 minutes, Home and End for the first or last free slot, and Enter or Space to open.`;
  }

  function blockBookingHint(block: CalendarBlockSpan, column: Column): BookLessonHint | null {
    // Blocks use half-open intervals: a start exactly at the end boundary is
    // outside the block, while every displayed quarter-hour before it is a
    // deliberate staff choice. All-day blocks use the exact visible Diary
    // range, not a hidden 09:00 default.
    const rangeStart = range.startHour * 60;
    const rangeEnd = range.endHour * 60;
    const blockStart = block.startTime === null ? rangeStart : block.startMinutes;
    const blockEnd = block.startTime === null ? rangeEnd : block.endMinutes;
    const firstMinute = Math.max(
      rangeStart,
      Math.ceil(blockStart / SLOT_MINUTES) * SLOT_MINUTES,
    );
    const lastMinute = Math.min(rangeEnd, blockEnd);
    const choices: { minute: number; iso: string }[] = [];

    for (let minute = firstMinute; minute < lastMinute; minute += SLOT_MINUTES) {
      // Do not offer an obvious real-lesson overlap as a block override. The
      // service duration is chosen later, so WMF still remains authoritative
      // about the complete requested interval at the exact write boundary.
      const occupiedByLesson = (byColumn.get(column.key) ?? []).some(
        (session) => minute >= session.startMinutes && minute < session.endMinutes,
      );
      if (occupiedByLesson) continue;

      const hour = Math.floor(minute / 60);
      const minutes = minute % 60;
      const attempt = londonWallClockAttempt(
        block.day,
        `${String(hour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
      );
      if (attempt.ok) choices.push({ minute, iso: attempt.iso });
    }

    if (choices.length === 0) return null;

    const preferredMinute = block.startTime === null
      ? Math.max(9 * 60, rangeStart)
      : block.startMinutes;
    const preferred = choices.find((choice) => choice.minute >= preferredMinute) ?? choices[0]!;
    return {
      startsAt: preferred.iso,
      startsAtOptions: choices.map((choice) => choice.iso),
      blockReason: block.reason,
      ...(column.tutorId ? { tutorId: column.tutorId } : {}),
    };
  }

  function beginDrag(event: DragEvent<HTMLElement>, session: Session<DiaryEntry>) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-wmf-appointment", session.appointmentId);
  }

  function dropLesson(
    event: DragEvent<HTMLElement>,
    column: Column,
    geometryNode?: HTMLElement,
    allowBlocked = false,
  ) {
    const appointmentId = event.dataTransfer.getData("application/x-wmf-appointment");
    if (!appointmentId) return;
    event.preventDefault();
    const target = event.target as Element;
    if (target.closest(".cal-session") || (!allowBlocked && target.closest(".cal-block"))) return;
    const session = sessions.find((candidate) => candidate.appointmentId === appointmentId);
    const grid = geometryNode ?? event.currentTarget;
    const result = targetAt(column, grid, event.clientY);
    if (!session || !result) return;

    // No optimistic repositioning. Closed-hour and blocked-time drops reach
    // the shared Move preview, which checks the exact instant and asks for a
    // schedule override only when the authoritative staff policy permits it.
    setSelectedBlank(null);
    showPointerCursor(null);
    showKeyboardCursor(null);
    setKeyboardFocusColumn(null);
    setSelected({ session, initialAction: "move", draggedTo: result.startsAt });
  }

  return (
    <>
      <div className={`cal-week${wholeDay ? " is-whole-day" : ""}`} ref={box} style={shape}>
        <div className="cal-week-head">
          <div className="cal-gutter-head" />
          {columns.map((column) => (
            <div
              className={`cal-day-head${column.highlight ? " is-today" : ""}`}
              key={column.key}
              title={column.heading}
            >
              <span className="cal-weekday">{column.heading}</span>
              {column.subheading !== undefined && (
                <>
                  {" "}<span className="cal-daynum">{column.subheading}</span>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="cal-week-body">
          <div className="cal-gutter" style={{ gridTemplateRows: `repeat(${rows}, var(--slot-h))` }}>
            {hours.map((hour, index) => (
              <span className="cal-hour" key={hour} style={{ gridRow: `${index * 4 + 1} / span 4` }}>
                {String(hour).padStart(2, "0")}:00
              </span>
            ))}
            {nowPosition !== null && (
              <span
                className="cal-now-label"
                role="img"
                aria-label={`Current time ${formatTime(now)}`}
                style={{ top: nowPosition }}
              >
                Now <time dateTime={now}>{formatTime(now)}</time>
              </span>
            )}
          </div>

          {columns.map((column) => {
            /**
             * When somebody is *not* available, drawn behind their lessons.
             *
             * The closed hours are shaded rather than the open ones lit, which
             * is the way round that composes: a column already carries hour
             * lines, a "today" tint and the lesson cards, and repainting the
             * open hours would have to sit correctly with all three, while a
             * wash over the closed ones sits behind everything and touches none
             * of it. The open time then looks exactly like an unshaded column,
             * which is what makes it read as available.
             *
             * Undefined is not an empty week. A tutor nobody has filled in gets
             * no shading at all and their column looks as it did before any of
             * this existed — shading a day shut because an owner has not got to
             * somebody yet would be a claim about a person that nobody made.
             */
            const open = hoursFor?.(column.key);
            const closed = open === undefined ? [] : gapsAround(open, range.startHour * 60, range.endHour * 60);
            // Presentation only: blockedByColumn still includes School spans
            // so hiding their duplicate cards never creates a free Add target.
            const blocked = (blockedByColumn.get(column.key) ?? []).filter(isVisibleCalendarBlock);
            const calendarItems: CalendarGridItem[] = [
              ...(byColumn.get(column.key) ?? []).map((session) => ({
                kind: "lesson" as const,
                startMinutes: session.startMinutes,
                endMinutes: session.endMinutes,
                session,
              })),
              ...blocked.map((block) => ({
                kind: "block" as const,
                startMinutes: block.startMinutes,
                endMinutes: block.endMinutes,
                block,
              })),
            ];
            const placedItems = layOutCalendarSpans(calendarItems);
            const firstBlank = firstFreeSlot(column);
            const storedKeyboardSlot = keyboardCursor?.columnKey === column.key
              ? freeSlotAtMinute(column, keyboardCursor.minute)
              : null;
            const keyboardSlot = storedKeyboardSlot ?? firstBlank;
            const pointerSlot = keyboardFocusColumn !== column.key && pointerCursor?.columnKey === column.key
              ? freeSlotAtMinute(column, pointerCursor.minute)
              : null;
            const keyboardRow = keyboardSlot
              ? Math.floor((keyboardSlot.minute - range.startHour * 60) / SLOT_MINUTES) + 1
              : null;
            const pointerRow = pointerSlot
              ? Math.floor((pointerSlot.minute - range.startHour * 60) / SLOT_MINUTES) + 1
              : null;
            return (
              <div
                className={`cal-day cal-day-interactive${column.highlight ? " is-today" : ""}`}
                key={column.key}
                style={{ gridTemplateRows: `repeat(${rows}, var(--slot-h))` }}
                onClick={(event) => openBlankCell(event, column)}
                onPointerCancel={(event) => finishBlankPointer(event, true)}
                onPointerDown={startBlankPointer}
                onPointerLeave={() => {
                  pointerRect.current = null;
                  showPointerCursor(null);
                }}
                onPointerMove={(event) => trackBlankPointer(event, column)}
                onPointerUp={(event) => finishBlankPointer(event)}
                onWheel={() => {
                  pointerRect.current = null;
                  showPointerCursor(null);
                }}
                onDragOver={(event) => { if (column.day) event.preventDefault(); }}
                onDrop={(event) => dropLesson(event, column)}
                title="Free quarter-hours: click or tap, or use the keyboard Add target, to book a lesson or block time; closed hours can be checked with a warning"
              >
                {closed.map((gap) => {
                  const { start, end } = rowsFor(
                    { startMinutes: gap.startsMinutes, endMinutes: gap.endsMinutes },
                    range,
                  );
                  return <div aria-hidden="true" className="cal-closed" key={`${gap.startsMinutes}-${gap.endsMinutes}`} style={{ gridRow: `${start} / ${end}` }} />;
                })}
                {column.day === today && nowPosition !== null && (
                  <div
                    className="cal-now-line"
                    aria-hidden="true"
                    style={{ top: nowPosition }}
                  />
                )}

                {pointerSlot && pointerRow !== null && (
                  <div
                    aria-hidden="true"
                    className="cal-blank-affordance"
                    style={{ gridRow: `${pointerRow} / span 1` }}
                  >
                    + Add
                  </div>
                )}

                {keyboardSlot && keyboardRow !== null && (
                  <button
                    aria-label={blankSlotLabel(column, keyboardSlot)}
                    className="cal-blank-keyboard-target"
                    style={{ gridRow: `${keyboardRow} / span 1` }}
                    type="button"
                    onBlur={() => {
                      if (keyboardFocusColumn === column.key) setKeyboardFocusColumn(null);
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      activateKeyboardSlot(column, keyboardSlot);
                    }}
                    onFocus={() => {
                      setKeyboardFocusColumn(column.key);
                      showKeyboardCursor({ columnKey: column.key, minute: keyboardSlot.minute });
                    }}
                    onKeyDown={(event) => handleBlankKeyDown(event, column, keyboardSlot)}
                  >
                    <span aria-hidden="true">+ Add</span>
                  </button>
                )}

                {placedItems.map(({ item, lane, lanes }) => {
                  const { start, end } = rowsFor(item, range);
                  const laneStyle = {
                    gridRow: `${start} / ${end}`,
                    marginLeft: `${(lane / lanes) * 100}%`,
                    width: `${(1 / lanes) * 100}%`,
                  };

                  if (item.kind === "block") {
                    const block = item.block;
                    const removing = blockBusyId === block.blockId;
                    const timing = blockedTimeLabel(block);
                    return (
                      <button
                        type="button"
                        disabled={removing}
                        aria-busy={removing || undefined}
                        aria-label={`Blocked time: ${block.reason}, ${describeCalendarBlock(block)}. Open actions.`}
                        className="cal-block"
                        key={`block:${block.blockId}:${block.day}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          // Explicit focus also covers browsers that do not focus
                          // buttons on pointer clicks; the Drawer restores it.
                          event.currentTarget.focus();
                          if (!removing) onBlockClick?.(block, blockBookingHint(block, column));
                        }}
                        onDragOver={(event) => {
                          // A block is not an empty booking cell, but an existing
                          // lesson may be deliberately moved into it. The Move
                          // flow validates the exact quarter-hour and preserves
                          // the block itself.
                          event.preventDefault();
                          event.stopPropagation();
                          event.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          const grid = event.currentTarget.closest(".cal-day");
                          dropLesson(
                            event,
                            column,
                            grid instanceof HTMLElement ? grid : undefined,
                            true,
                          );
                        }}
                        style={laneStyle}
                        title={`${block.reason} · ${timing}`}
                      >
                        <span className="cal-block-label">{removing ? "Removing…" : block.reason}</span>
                        {!removing && <span className="cal-block-time">{timing}</span>}
                      </button>
                    );
                  }

                  const session = item.session;
                  return (
                    <SessionBox
                      key={`lesson:${session.appointmentId}`}
                      session={session}
                      next={session.appointmentId === focusAppointmentId}
                      payment={payments.get(ameliaAppointmentIdOf(session) ?? -1)}
                      onOpen={() => {
                        setSelectedBlank(null);
                        showPointerCursor(null);
                        showKeyboardCursor(null);
                        setKeyboardFocusColumn(null);
                        setBookingHint(null);
                        setSelected({ session, initialAction: null, draggedTo: null });
                      }}
                      onDragStart={(event) => beginDrag(event, session)}
                      style={laneStyle}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {selectedBlank && (
        <BlankSlotDrawer
          slot={selectedBlank}
          target={resolveBlockTarget?.(selectedBlank.tutorId) ?? null}
          targetLoading={blockTargetLoading}
          targetError={blockTargetError}
          suggestionEntries={entries}
          suggestionRange={missingBookingSuggestionRange(selectedBlank, loadedBookingRange)}
          onClose={() => setSelectedBlank(null)}
          onBooked={() => {
            setSelectedBlank(null);
            refresh(onScheduleChanged);
          }}
          onBlocked={(block) => {
            setSelectedBlank(null);
            onBlockCreated?.(block.provider_id);
          }}
        />
      )}

      {selected && (
        <LessonActionDrawer
          key={`${selected.session.appointmentId}:${selected.initialAction ?? "details"}:${selected.draggedTo ?? "click"}`}
          session={selected.session}
          initialAction={selected.initialAction}
          initialMoveTarget={selected.draggedTo}
          initialPayment={payments.get(ameliaAppointmentIdOf(selected.session) ?? -1) ?? null}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            refresh();
          }}
          onRefresh={() => { void refreshSelectedInPlace(selected.session); }}
          onScheduleChanged={(change) => {
            setSelected(null);
            refresh(() => onScheduleChanged?.(change));
          }}
        />
      )}

      {bookingHint && (
        <BookLessonDrawer
          key={`${bookingHint.tutorId ?? "choose"}:${bookingHint.startsAt}`}
          hint={bookingHint}
          onClose={() => setBookingHint(null)}
          onBooked={() => {
            // The drawer forwards its receipt to the page as it closes.
            setBookingHint(null);
            refresh(onScheduleChanged);
          }}
        />
      )}
    </>
  );
}

function writeUpState(entry: DiaryEntry & { anonymous: false }): string {
  if (entry.lessonId === null) return "not written up";
  return entry.lessonPublishedAt === null ? "notes in draft" : "notes published";
}

/**
 * One lesson in the grid. Readable lessons are buttons: clicking one opens the
 * same appointment drawer used everywhere else, while drag-to-reschedule opens
 * that drawer directly in its Move flow. Colleagues remain inert.
 *
 * The corner payment marker is a live booking-service read, not stored state.
 * `payment` comes from the live classification map the Diary hook clears
 * before every refresh, so the grid never keeps asserting stale payment truth
 * while a new classification is loading.
 */
function SessionBox({
  session,
  next,
  payment,
  style,
  onOpen,
  onDragStart,
}: {
  session: Session<DiaryEntry>;
  next: boolean;
  payment: DiaryPaymentClassification | undefined;
  style: React.CSSProperties;
  onOpen: () => void;
  onDragStart: (event: DragEvent<HTMLElement>) => void;
}) {
  const first = session.bookings[0];
  if (!first) return null;
  const service = first.serviceName ?? "Lesson";
  const instrument = first.instrument?.trim() || null;
  const when = `${formatTime(session.startsAt)}–${formatTime(session.endsAt)}`;
  const painted = { ...style, ...colourOf(first) };
  const colourClass = hasColour(first) ? " has-colour" : "";

  if (first.anonymous) {
    return (
      <div className={`cal-session is-other${colourClass}`} style={painted} title={`${when} · ${first.tutorName ?? "No tutor recorded"} · ${service}`}>
        <span className="cal-session-line">
          <time dateTime={session.startsAt}>{formatTime(session.startsAt)}</time>{" "}
          <span className="cal-session-who">{first.tutorName ?? "No tutor recorded"}</span>
        </span>
        <span className="cal-session-detail">{service}</span>
      </div>
    );
  }

  const mine = session.bookings.filter(
    (entry): entry is DiaryEntry & { anonymous: false } => !entry.anonymous,
  );
  const solo = mine.length === 1;
  const syncing = mine.some((entry) => "syncing" in entry && entry.syncing === true);
  const noShow = isNoShowSession(session);
  const showNext = next && !noShow;
  const attended = mine.find((entry) => !isNoShow(entry));
  const classes = ["cal-session", "cal-session-button"];
  if (solo) classes.push("is-solo");
  if (syncing) classes.push("is-syncing");
  if (noShow) classes.push("is-no-show");
  else if (mine.some(isNoShow)) classes.push("has-no-show-participants");
  if (showNext) classes.push("is-next");
  if (hasColour(first)) classes.push("has-colour");
  if (attended && attended.lessonId !== null) classes.push(attended.lessonPublishedAt === null ? "is-draft" : "is-done");
  if (solo && first.studentId === null && !first.isSchools) classes.push("is-unmatched");
  const paymentIndicator = diaryPaymentIndicator(payment, session.endsAt);

  const heading = (
    <span className="cal-session-line">
      <time dateTime={session.startsAt}>{formatTime(session.startsAt)}</time>{" "}
      {noShow && <span className="cal-no-show-marker" role="img" aria-label="No-show">⊘ </span>}
      <span className="cal-session-who">
        {solo ? (nameOf(first) ?? "Unknown") : noShow
          ? <span className="cal-no-show-pill cal-no-show-group-label">No-show</span>
          : service}
        {solo && !noShow && first.lessonPublishedAt !== null && " ✓"}
      </span>
    </span>
  );
  const detail = (
    <span className="cal-session-detail">
      {solo && noShow && <span className="cal-no-show-detail"><span className="cal-no-show-pill">No-show</span></span>}
      {solo ? (
        <>{!noShow && service}</>
      ) : (
        mine.map((entry) => (
          <span className={`cal-session-line${isNoShow(entry) ? " is-no-show-participant" : ""}`} key={entry.bookingId}>
            {isNoShow(entry) && <span role="img" aria-label="No-show">⊘ </span>}
            {nameOf(entry) ?? "Unknown"}{!isNoShow(entry) && entry.lessonPublishedAt !== null && " ✓"}
          </span>
        ))
      )}
    </span>
  );

  const draggable = !noShow && isAmeliaAppointmentId(first.externalId);
  const state = noShow ? "no-show" : first.isSchools
    ? "School lesson"
    : first.studentId === null
      ? "not matched to a student"
      : writeUpState(attended ?? first);
  const who = solo ? nameOf(first) ?? "Unknown" : `${mine.length} students`;
  const title = `${when} · ${who} · ${service} · ${state}`;
  const participants = solo ? "" : ` · ${mine.map((entry) => `${nameOf(entry) ?? "Unknown"}${isNoShow(entry) ? " · no-show" : ""}`).join(", ")}`;
  return (
    <button
      data-appointment-id={session.appointmentId}
      className={classes.join(" ")}
      style={{ ...painted, position: "relative" }}
      title={title}
      aria-label={`${title}${participants}${showNext ? " · Next lesson" : ""}${paymentIndicator ? ` · ${paymentIndicator.label}` : ""}${syncing ? " · Syncing" : ""}`}
      type="button"
      draggable={draggable}
      onClick={(event) => { event.stopPropagation(); onOpen(); }}
      onDragStart={draggable ? onDragStart : undefined}
    >
      {showNext && <span className="cal-next-label">Next lesson</span>}
      {paymentIndicator !== null && (
        <span
          className={`cal-payment-indicator is-${paymentIndicator.tone}`}
          aria-label={paymentIndicator.label}
          title={paymentIndicator.label}
        >
          {paymentIndicator.symbol}
        </span>
      )}
      {heading}
      {detail}
      {syncing && <span className="visually-hidden">Syncing</span>}
      {instrument && !noShow && <span className="cal-session-mobile-instrument">{instrument}</span>}
    </button>
  );
}

function nameOf(entry: DiaryEntry & { anonymous: false }): string | null {
  return entry.studentName ?? entry.customerName;
}
